import React from "react";
import { Observer } from "mobx-react";
import locale from "@bonliva-crm/locale";
import {
  Customer,
  CustomerSearch_Count,
  CustomerSearch_Search,
  NodeRefFragment,
  NodeType,
  SimpleCustomerCardFragment
} from "@graphql";
import { store } from "@store";
import { getSortOptions, SortOption, sortOptionToGql } from "@search/Utils";
import { searchFactory } from "@search/Search";
import { GeneralSearchData, GeneralSearchStore } from "@search/GeneralSearchStore";
import GroupHeadline from "@components/GroupHeadline";
import { searchToFilter } from "./utils";
import { CustomerCardSubscriber } from "../CustomerCard/CustomerCardSubscriber";
import { CustomerSearchActionBar } from "./CustomerSearchActionBar";

export type CustomerData = CustomerSearch_Search.Return["customers"]["items"][0];

const Search = searchFactory();

type Props = {
  onCustomerOpen: (id: string) => any;
  onNewItem: () => any;
  onNodeClick: (nodeRef: NodeRefFragment) => void;
};

type State = {};

class CustomerSearch extends React.Component<Props, State> {
  state: State = {};

  currentDataSet: SimpleCustomerCardFragment[] = [];

  fetchData = (skip: number, searchStore: GeneralSearchStore) => {
    if (skip === 0) {
      this.currentDataSet = [];
    }

    return CustomerSearch_Search.func(
      {
        filter: searchToFilter(searchStore.searchParams, searchStore),
        skip,
        limit: 20,
        ...sortOptionToGql(searchStore.sortOption)
      },
      { fetchPolicy: "network-only" }
    ).then((x) => {
      this.currentDataSet.push(...x.data.customers.items);
      return x.data.customers?.items;
    });
  };

  fetchCount = (searchStore: GeneralSearchStore) =>
    CustomerSearch_Count.func(
      { filter: searchToFilter(searchStore.searchParams, searchStore) },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => (searchStore.count = data.customersTotal.total));

  normalizeRegion(customer: CustomerData) {
    if (!customer.Address) {
      return "";
    }

    return [customer.Address.Country, customer.Address.Region]
      .filter((x) => x)
      .join(":")
      .toUpperCase();
  }

  renderActionBar = (search: GeneralSearchData, count: number | null) => {
    const { generalSearchStore } = store();

    return (
      <Observer>
        {() => {
          const sortOptions = getSortOptions(generalSearchStore.sortOption);

          return (
            <CustomerSearchActionBar
              onNew={this.props.onNewItem}
              total={generalSearchStore.count}
              sortOptions={sortOptions}
              onSortOption={(opt) =>
                (generalSearchStore.sortOption = opt.value as SortOption)
              }
            />
          );
        }}
      </Observer>
    );
  };

  render() {
    return (
      <Search
        SearchPageLayoutProps={{
          ClearSearchProps: {
            style: {
              position: "static"
            }
          }
        }}
        nodeType={NodeType.Customer}
        fetchData={this.fetchData}
        fetchCount={this.fetchCount}
        title={locale.Customers}
        renderActionBar={(search, searchStore, { count }) =>
          this.renderActionBar(search, count)
        }
        renderDetail={({ item, search: { text } }) => (
          <CustomerCardSubscriber
            id={(item as CustomerData)._id}
            highlightSearch={text.trim()}
            onClick={() => this.props.onCustomerOpen((item as CustomerData)._id)}
            onParentClick={(id) => this.props.onCustomerOpen(id)}
            includeContactPersons
            asLink={true}
          />
        )}
        group={{
          options: [],
          by: () => this.normalizeRegion,
          render: (customer: Customer) => (
            <GroupHeadline
              mainText={customer.Address.Region}
              smallText={customer.Address.Country}
            />
          )
        }}
      >
        {(p) => {
          return (
            <>
              {p.responsiblePerson} {p.customerType} {p.areasOfExpertise} {p.region}
              {p.knownSystems} {p.lastContact} {p.organizationType}
              {p.callLists} {p.customerNumbers} {p.journalSystem}
            </>
          );
        }}
      </Search>
    );
  }
}

export default CustomerSearch;
